import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft, MessageSquare, Search, RefreshCw, Check, X, Clock,
  AlertCircle, CheckCircle, Eye, Edit3, Pin, PinOff, Trash2,
  Image, User, Calendar, Loader2, FileText, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  pending: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: X }
};

const CommunityFeedModeration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [pinnedPosts, setPinnedPosts] = useState([]);
  const [filters, setFilters] = useState({
    status: 'pending',
    search: ''
  });
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedPost, setSelectedPost] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    pinned: 0
  });

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, approvedRes, pinnedRes] = await Promise.all([
        api.get('/feed/admin/pending'),
        api.get('/feed?limit=50'),  // Get approved posts from main feed
        api.get('/feed/admin/pinned').catch(() => ({ data: [] }))
      ]);

      // admin/pending returns array directly
      const pendingPosts = Array.isArray(pendingRes.data) ? pendingRes.data : [];
      // Main feed returns approved posts
      const approvedPosts = approvedRes.data?.posts || [];
      const pinnedPostsList = Array.isArray(pinnedRes.data) ? pinnedRes.data : [];
      
      // Set posts based on active tab
      if (activeTab === 'pending') {
        setPosts(pendingPosts);
      } else if (activeTab === 'approved') {
        setPosts(approvedPosts.filter(p => p.status === 'approved'));
      } else {
        setPosts([]);  // Pinned tab uses pinnedPosts state
      }
      
      setPinnedPosts(pinnedPostsList);

      setStats({
        pending: pendingPosts.length,
        approved: approvedPosts.length,
        rejected: 0,
        pinned: pinnedPostsList.length
      });
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const openEditDialog = (post) => {
    setSelectedPost(post);
    setEditedContent(post.content);
    setEditDialog(true);
  };

  const openRejectDialog = (post) => {
    setSelectedPost(post);
    setRejectionReason('');
    setRejectDialog(true);
  };

  const openDeleteDialog = (post) => {
    setSelectedPost(post);
    setDeleteDialog(true);
  };

  const handleApprove = async (post, withEdit = false) => {
    setProcessing(true);
    try {
      const payload = { action: 'approve' };
      if (withEdit && editedContent !== post.content) {
        payload.edited_content = editedContent;
      }

      await api.patch(`/feed/admin/${post.id}/moderate`, payload);
      toast.success(withEdit ? 'Post approved with edits!' : 'Post approved!');
      setEditDialog(false);
      setSelectedPost(null);
      fetchPosts();
    } catch (error) {
      console.error('Failed to approve post:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve post');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await api.patch(`/feed/admin/${selectedPost.id}/moderate`, {
        action: 'reject',
        rejection_reason: rejectionReason || null
      });
      toast.success('Post rejected');
      setRejectDialog(false);
      setSelectedPost(null);
      fetchPosts();
    } catch (error) {
      console.error('Failed to reject post:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject post');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    setProcessing(true);
    try {
      await api.delete(`/feed/admin/${selectedPost.id}`);
      toast.success('Post deleted permanently');
      setDeleteDialog(false);
      setSelectedPost(null);
      fetchPosts();
    } catch (error) {
      console.error('Failed to delete post:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    } finally {
      setProcessing(false);
    }
  };

  const handleTogglePin = async (post) => {
    setProcessing(true);
    try {
      await api.patch(`/feed/admin/${post.id}/pin`, {
        pinned: !post.is_pinned
      });
      toast.success(post.is_pinned ? 'Post unpinned' : 'Post pinned!');
      fetchPosts();
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      toast.error(error.response?.data?.detail || 'Failed to update pin status');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveEdit = async () => {
    setProcessing(true);
    try {
      await api.patch(`/feed/admin/${selectedPost.id}/moderate`, {
        action: 'edit',
        edited_content: editedContent
      });
      toast.success('Edit saved (post still pending)');
      setEditDialog(false);
      setSelectedPost(null);
      fetchPosts();
    } catch (error) {
      console.error('Failed to save edit:', error);
      toast.error(error.response?.data?.detail || 'Failed to save edit');
    } finally {
      setProcessing(false);
    }
  };

  const filteredPosts = posts.filter(post => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        post.content?.toLowerCase().includes(search) ||
        post.author_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="community-moderation">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin/home')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <MessageSquare className="w-6 h-6 text-blue-500" />
                  Community Feed Moderation
                </h1>
                <p className="text-muted-foreground">Review, edit, and manage talent posts</p>
              </div>
            </div>
            <Button onClick={fetchPosts} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold">{stats.approved}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Pin className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pinned Posts</p>
                <p className="text-2xl font-bold">{stats.pinned}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Content Control</p>
                <p className="text-2xl font-bold">Active</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white">
            <TabsTrigger value="pending" className="data-[state=active]:bg-amber-100">
              <Clock className="w-4 h-4 mr-1" />
              Pending ({stats.pending})
            </TabsTrigger>
            <TabsTrigger value="approved" className="data-[state=active]:bg-green-100">
              <CheckCircle className="w-4 h-4 mr-1" />
              Approved ({stats.approved})
            </TabsTrigger>
            <TabsTrigger value="pinned" className="data-[state=active]:bg-blue-100">
              <Pin className="w-4 h-4 mr-1" />
              Pinned ({stats.pinned})
            </TabsTrigger>
          </TabsList>

          {/* Search */}
          <Card className="bg-white mb-6">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search posts by content or author..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Pending Posts */}
          <TabsContent value="pending">
            <div className="space-y-4">
              {filteredPosts.length === 0 ? (
                <Card className="bg-white">
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                    <p className="font-medium text-lg">All caught up!</p>
                    <p className="text-muted-foreground">No posts pending review</p>
                  </CardContent>
                </Card>
              ) : (
                filteredPosts.map((post) => (
                  <PostCard 
                    key={post.id} 
                    post={post} 
                    onEdit={() => openEditDialog(post)}
                    onApprove={() => handleApprove(post)}
                    onReject={() => openRejectDialog(post)}
                    onDelete={() => openDeleteDialog(post)}
                    onTogglePin={() => handleTogglePin(post)}
                    processing={processing}
                    showModerateActions={true}
                  />
                ))
              )}
            </div>
          </TabsContent>

          {/* Approved Posts */}
          <TabsContent value="approved">
            <div className="space-y-4">
              {filteredPosts.length === 0 ? (
                <Card className="bg-white">
                  <CardContent className="p-8 text-center text-muted-foreground">
                    No approved posts found
                  </CardContent>
                </Card>
              ) : (
                filteredPosts.map((post) => (
                  <PostCard 
                    key={post.id} 
                    post={post} 
                    onEdit={() => openEditDialog(post)}
                    onDelete={() => openDeleteDialog(post)}
                    onTogglePin={() => handleTogglePin(post)}
                    processing={processing}
                    showModerateActions={false}
                  />
                ))
              )}
            </div>
          </TabsContent>

          {/* Pinned Posts */}
          <TabsContent value="pinned">
            <div className="space-y-4">
              {pinnedPosts.length === 0 ? (
                <Card className="bg-white">
                  <CardContent className="p-8 text-center text-muted-foreground">
                    <Pin className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p className="font-medium">No pinned posts</p>
                    <p className="text-sm">Pin important announcements to keep them at the top</p>
                  </CardContent>
                </Card>
              ) : (
                pinnedPosts.map((post, index) => (
                  <PostCard 
                    key={post.id} 
                    post={post} 
                    pinIndex={index + 1}
                    onEdit={() => openEditDialog(post)}
                    onDelete={() => openDeleteDialog(post)}
                    onTogglePin={() => handleTogglePin(post)}
                    processing={processing}
                    showModerateActions={false}
                  />
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5" />
              Edit Post Before Approval
            </DialogTitle>
            <DialogDescription>
              Edit the content to remove personal information or clean up messaging.
              The edited version will be published, not the original.
            </DialogDescription>
          </DialogHeader>
          {selectedPost && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Author: <span className="font-medium text-foreground">{selectedPost.author_name}</span></p>
                <p className="text-xs text-muted-foreground">Submitted: {new Date(selectedPost.created_at).toLocaleString()}</p>
              </div>
              
              {selectedPost.original_content && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs font-medium text-amber-800 mb-1">Original (Previously Edited):</p>
                  <p className="text-sm text-amber-900">{selectedPost.original_content}</p>
                </div>
              )}
              
              <div>
                <Label>Post Content</Label>
                <Textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  rows={6}
                  className="mt-1 font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Remove phone numbers, emails, or inappropriate content before approving.
                </p>
              </div>

              {selectedPost.media_urls?.length > 0 && (
                <div>
                  <Label>Attached Media ({selectedPost.media_urls.length})</Label>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {selectedPost.media_urls.map((url, i) => (
                      <img key={i} src={url} alt={`Media ${i+1}`} className="w-20 h-20 object-cover rounded" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setEditDialog(false)} disabled={processing}>
              Cancel
            </Button>
            <Button variant="outline" onClick={handleSaveEdit} disabled={processing}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              Save Edit (Keep Pending)
            </Button>
            <Button 
              className="bg-green-500 hover:bg-green-600" 
              onClick={() => handleApprove(selectedPost, true)}
              disabled={processing}
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Approve {editedContent !== selectedPost?.content ? 'with Edits' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Post</DialogTitle>
            <DialogDescription>
              Optionally provide a reason for rejection.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Rejection Reason (Optional)</Label>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Content violates community guidelines..."
              rows={3}
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)} disabled={processing}>
              Cancel
            </Button>
            <Button 
              className="bg-red-500 hover:bg-red-600" 
              onClick={handleReject}
              disabled={processing}
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
              Reject Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Post Permanently</DialogTitle>
            <DialogDescription>
              This action cannot be undone. The post and all associated data will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          {selectedPost && (
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-800 font-medium mb-1">Post by: {selectedPost.author_name}</p>
              <p className="text-sm text-red-700 line-clamp-3">{selectedPost.content}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)} disabled={processing}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700" 
              onClick={handleDelete}
              disabled={processing}
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Post Card Component
const PostCard = ({ post, pinIndex, onEdit, onApprove, onReject, onDelete, onTogglePin, processing, showModerateActions }) => {
  const status = statusConfig[post.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <Card className={`bg-white ${post.is_pinned ? 'border-blue-300 bg-blue-50/30' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            {/* Post Header */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                {post.author_photo ? (
                  <img src={post.author_photo} alt={post.author_name} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-slate-500" />
                )}
              </div>
              <div>
                <p className="font-medium">{post.author_name}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Calendar className="w-3 h-3" />
                  {new Date(post.created_at).toLocaleString()}
                </div>
              </div>
              <Badge className={status.color}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </Badge>
              {post.is_pinned && (
                <Badge className="bg-blue-100 text-blue-700">
                  <Pin className="w-3 h-3 mr-1" />
                  Pinned {pinIndex ? `#${pinIndex}` : ''}
                </Badge>
              )}
              {post.edited_by_admin && (
                <Badge className="bg-purple-100 text-purple-700">
                  <Edit3 className="w-3 h-3 mr-1" />
                  Admin Edited
                </Badge>
              )}
            </div>

            {/* Post Content */}
            <div className="mb-3">
              <p className="text-sm whitespace-pre-wrap">{post.content}</p>
              {post.original_content && (
                <details className="mt-2">
                  <summary className="text-xs text-amber-600 cursor-pointer">View original content</summary>
                  <p className="text-xs text-muted-foreground mt-1 p-2 bg-amber-50 rounded">{post.original_content}</p>
                </details>
              )}
            </div>

            {/* Media */}
            {post.media_urls?.length > 0 && (
              <div className="flex gap-2 mb-3 flex-wrap">
                {post.media_urls.slice(0, 4).map((url, i) => (
                  <img key={i} src={url} alt={`Media ${i+1}`} className="w-16 h-16 object-cover rounded" />
                ))}
                {post.media_urls.length > 4 && (
                  <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center text-sm text-muted-foreground">
                    +{post.media_urls.length - 4}
                  </div>
                )}
              </div>
            )}

            {/* Engagement Stats */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>{post.likes_count || 0} likes</span>
              <span>{post.comments_count || 0} comments</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button variant="outline" size="sm" onClick={onEdit} disabled={processing}>
              <Edit3 className="w-4 h-4" />
            </Button>
            {showModerateActions && (
              <>
                <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={onApprove} disabled={processing}>
                  <Check className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={onReject} disabled={processing}>
                  <X className="w-4 h-4" />
                </Button>
              </>
            )}
            {post.status === 'approved' && (
              <Button 
                variant="outline" 
                size="sm" 
                className={post.is_pinned ? 'text-blue-600 bg-blue-50' : ''}
                onClick={onTogglePin} 
                disabled={processing}
              >
                {post.is_pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
              </Button>
            )}
            <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={onDelete} disabled={processing}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CommunityFeedModeration;
